import React, { useState, useEffect } from 'react';
import { ShieldCheck, Compass, ExternalLink, Zap, Layers, Award, Sparkles } from 'lucide-react';

const TICKET_SERVICE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://aviatricks.in');
const AGRISIM_URL = window.location.origin + '/businesses/agri-sim';

export default function AgriSimPlayerPage() {
  const [streamId, setStreamId] = useState('STRM-' + Math.random().toString(36).substring(2, 8).toUpperCase());
  const [loading, setLoading] = useState(false);

  // Background PostMessage Listener for in-engine flight help events
  useEffect(() => {
    function handleEngineMessage(event) {
      const { type } = event.data || {};
      if (type !== 'AGRISIM_HELP') return;

      const newTab = window.open('about:blank', '_blank');
      handleHelpTrigger(newTab);
    }

    window.addEventListener('message', handleEngineMessage);
    return () => window.removeEventListener('message', handleEngineMessage);
  }, [streamId]);

  const handleHelpTrigger = async (targetTab = null) => {
    setLoading(true);

    try {
      const res = await fetch(`${TICKET_SERVICE_URL}/api/support-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ streamId }),
      });

      if (!res.ok) throw new Error(`Token request failed: ${res.status}`);

      const { token } = await res.json();
      const destUrl = `${AGRISIM_URL}?support=open&token=${encodeURIComponent(token)}`;

      if (targetTab) {
        targetTab.location.href = destUrl;
      } else {
        window.location.href = destUrl;
      }
    } catch (err) {
      if (targetTab) targetTab.close();
      alert(`Unable to open AgriSim support ticket: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleManualLaunch = () => {
    window.postMessage({ type: 'AGRISIM_HELP' }, '*');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* HERO INTRO */}
        <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10 shadow-sm relative overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3.5 py-1 bg-purple-100 text-purple-700 font-bold text-xs rounded-full uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Next-Gen Agri Flight Platform
            </span>
            <span className="text-xs text-gray-400 font-mono">Session ID: {streamId}</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight">
            Agri Drone Simulator
          </h1>
          
          <p className="text-base text-gray-600 mt-3 leading-relaxed max-w-2xl">
            Welcome to Aviatricks Aero Labs' flagship flight simulator. Built on Unreal Engine 5 with real-world aerodynamics, AgroFlyte enables pilot trainees to master field spraying, crop mapping, and autonomous precision flying in a risk-free virtual environment.
          </p>

          {/* MAIN ACTION BUTTON */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={handleManualLaunch}
              disabled={loading}
              className="px-8 py-4 bg-purple-700 hover:bg-purple-800 text-white rounded-2xl font-bold text-sm tracking-wide transition shadow-lg flex items-center gap-2.5 disabled:opacity-50"
            >
              {loading ? (
                "Connecting to Support Portal..."
              ) : (
                <>
                  🎮 Launch Support Ticket & Open AgriSim
                  <ExternalLink className="w-4 h-4" />
                </>
              )}
            </button>

            <a
              href={AGRISIM_URL}
              target="_blank"
              rel="noreferrer"
              className="px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-semibold text-sm transition"
            >
              Explore AgriSim Features &rarr;
            </a>
          </div>
        </div>

        {/* GUIDANCE & FEATURES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center font-bold mb-4">
              <Compass className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Real-World Aerodynamics</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Every throttle input, drift angle, and wind response replicates actual field flight dynamics developed alongside experienced agri drone pilots.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center font-bold mb-4">
              <Layers className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Multi-Crop Stages</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Train across seeding, pre-harvest, and harvest growth cycles to adapt flight altitudes, spray swath widths, and obstacle avoidance techniques.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center font-bold mb-4">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Instant Pilot Support</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Facing a flight anomaly or simulation issue? Clicking the support trigger connects your flight session directly with our engineering team.
            </p>
          </div>
        </div>

        {/* GUIDANCE OVERVIEW CARD */}
        <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-xs font-bold text-purple-700 uppercase tracking-wider">
            <Award className="w-4 h-4" /> Pilot Flight Guidance
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            Getting Started with AgroFlyte Training
          </h2>
          <p className="text-xs text-gray-600 leading-relaxed">
            Connect your standard RC transmitter or GCS controller to begin virtual flight exercises. Practice low-altitude agricultural spraying passes, terrain-following, and automatic return-to-launch procedures. If any technical issues occur during your session, click the support button above to log a ticket immediately.
          </p>
        </div>

      </div>
    </div>
  );
}
